import { Switch } from '@/components/ui/switch';
import type { AttributeNode } from '@/types/schemas';
import { ChevronDown, ChevronRight } from 'lucide-react';

export function SchemaFieldRow(props: {
  node: AttributeNode;
  path: string;
  depth: number;
  included: Record<string, boolean>;
  onToggle: (path: string, next: boolean) => void;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  disabled?: boolean;
  hasChildren: boolean;
  children?: React.ReactNode;
  open: boolean;
  onToggleOpen: () => void;
}) {
  const {
    node,
    path,
    depth,
    included,
    onToggle,
    selectedPath,
    onSelect,
    disabled = false,
    hasChildren,
    children,
    open,
    onToggleOpen,
  } = props;

  const isIncluded = node.required || (included[path] ?? false);
  const isSelected = selectedPath === path;

  return (
    <div className="space-y-2">
      <div
        className={[
          'flex items-center justify-between gap-3 rounded-xl border px-3 py-2',
          isSelected ? 'ring-2 ring-offset-2' : '',
        ].join(' ')}
        onMouseDown={() => onSelect(path)}
      >
        <div className="flex items-center gap-2" style={{ paddingLeft: depth * 12 }}>
          {hasChildren ? (
            <button
              type="button"
              className="hover:bg-muted rounded-md p-1"
              onClick={onToggleOpen}
              onMouseDown={(e) => e.stopPropagation()}
              aria-label="Toggle"
            >
              {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="inline-block h-6 w-6" />
          )}

          <button
            type="button"
            className="text-left"
            onMouseDown={(e) => {
              e.stopPropagation();
              onSelect(path);
            }}
          >
            <div className="flex items-center gap-2">
              <span className="font-mono text-sm">{node.key}</span>
              <span className="text-muted-foreground rounded-md border px-2 py-0.5 text-xs">
                {node.type}
                {node.nullable ? ' | null' : ''}
                {node.type === 'string' && node.enum?.length ? ` (enum)` : ''}
              </span>
              {node.required ? <span className="text-muted-foreground text-xs">required</span> : null}
            </div>
            <div className="text-muted-foreground text-xs">{path}</div>
          </button>
        </div>

        <Switch
          checked={isIncluded}
          onCheckedChange={(v) => {
            if (node.required) return; // required cannot be turned off
            onToggle(path, v);
          }}
          disabled={disabled || node.required}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </div>

      {open && hasChildren ? <div className="pl-6">{children}</div> : null}
    </div>
  );
}
